import { Transport, TransportEvents } from './transport';

import { NestedTypedEventEmitter3 } from './event-emitter';
import { Nullable } from './utils';
import { LogEvent } from './log';

import * as bowser from 'bowser';

const userAgentParsed = bowser.parse(navigator.userAgent);
const isChromeUa = !! userAgentParsed.browser.name?.startsWith('Chrome');

export type ClientEvents = TransportEvents & LogEvent;

export class Client extends NestedTypedEventEmitter3<ClientEvents> {

  private _transport: Nullable<Transport>;
  private _serverUrl = '';

  constructor() {
    super();

    this._transport = new Transport();
    this._transport.on('message',
      (msg, groupId, tag, streamIdx) => this.emit('message', msg, groupId, tag, streamIdx));

    this._transport.on('error',
      (err: Error, innerErr?: Error) => this.emit('error', err, innerErr));

    this._transport.on('ready', () => this.emit('ready'));

    this._transport.on('closed', () => {
      this.emit('closed');
      this._events.removeAllListeners();
    });

    this._transport.on('log', (log: string) => this.emit('log', log));
  }

  get isDisposed(): boolean {
    return ! this._transport;
  }

  get isConnected(): boolean {
    return !! this._transport?.isConnectionReady();
  }

  async dispose() {
    if (!this._transport) return;
    this.emit('log', 'Client disposal ...');
    await this._transport.disconnect();
    this._transport = null;
    this.emit('log', 'Client disposed');
  }

  async connect(serverUrl: string) {
    if (this.isDisposed) throw new Error('Can\'t call connect, client disposed');
    this._serverUrl = serverUrl;

    try {
      await this._transport?.connect(this._serverUrl);
    } catch(err) {
      this.emit('error', err as Error);
      return;
    }
    if (!this.isConnected) {
      return;
    }

    return this._transport?.accept();
  }

  async getTransportConnectionStats(): Promise<string | WebTransportStats> {
    if (!this._transport) throw new Error('Can\'t call getTransportConnectionStats, client disposed');
    if (isChromeUa) {
      return 'Chrome does not support WebTransport API getStats method yet';
    }
    return this._transport.getConnectionStats();
  }

  getTransportStatusInfo() {
    return this._transport?.getStatusInfo() || null;
  }

  getTransportStats() {
    return this._transport?.getStats();
  }

  getVideoCodecInfo() {
    return this._transport?.getVideoCodecInfo();
  }

  getAudioCodecInfo() {
    return this._transport?.getAudioCodecInfo();
  }
}

